import { Button, Flex, Input } from "antd";
import { useFormik } from "formik";
import { ArrowLeft, InfoIcon } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useAuthenticate } from "../../../api/api";
import { useUpdatePassword } from "../../../api/api.update";
import ErrorPanel from "../../../components/error.panel.component";
import { useMessage } from "../../../components/notifications/message";
import { useLayoutContext } from "../../../providers/layout.provider";
import { IStatusMsg } from "../../../types/types";

export default function ChangePasswordPage() {
  const { isMobile } = useLayoutContext();
  const { mutateAsync, isLoading, isError } = useAuthenticate();
  const navigate = useNavigate();
  const { notification, messageSuccess, messageError } = useMessage();
  const [statusMsg, setStatusMsg] = useState<IStatusMsg | undefined>();
  const { mutateAsync: updatePasswordAsync } = useUpdatePassword();

  const formik = useFormik({
    initialValues: {
      password: "",
      newPassword: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      password: Yup.string().required("Please enter password."),
      newPassword: Yup.string().required("Please enter new password."),
      confirmPassword: Yup.string()
        .required("Please confirm new password.")
        .oneOf([Yup.ref("newPassword")], "Passwords do not match"),
    }),
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      updatePasswordAsync({
        newPassword: values.newPassword,
        password: values.password,
      })
        .then((resp) => {
          messageSuccess("Password successfully updated");
        })
        .catch((e) => {
          if (e.response.status === 400) {
            messageSuccess("Please verify your current password.");
          } else {
            messageError("Whoops! Something went wrong. Please try again.");
          }
        })
        .finally(() => formik.resetForm());
    },
  });

  return (
    <div className="mx-auto max-w-5xl min-h-screen">
      <div className="divCenterAlign divColumn">
        {notification}

        <div className="flex justify-between px-6 py-4 w-full bg-gray-100">
          <Link
            to="/home"
            className="flex items-center gap-2 hover:opacity-80 transition"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
            <span className="text-black font-bold">Back</span>
          </Link>
        </div>

        <div className="bg-blue-100 border border-blue-300 rounded-lg p-3 flex gap-2 my-10 text-sm max-w-xl items-center">
          <InfoIcon size={30} />
          <p className="flex justify-start text-left font-bold">
            Incase if you feel your credentials are compromised, please reset
            immediately.
          </p>
        </div>

        <div
          className="mb-80"
          style={{
            backgroundColor: "#f4f7f8",
            flex: 1,
            border: "1px solid darkgrey",
            borderRadius: 10,
            padding: "10px 40px",
          }}
        >
          <h1 className="text-3xl font-bold text-gray-900 mt-10">
            Change Password
          </h1>
          <form
            onSubmit={formik.handleSubmit}
            className="divColumn divCenterAlign px-0 pb-10"
          >
            <Flex vertical className="p-20" gap={30}>
              <Input.Password
                {...formik.getFieldProps("password")}
                status={!!formik.errors.password ? "error" : undefined}
                size="large"
                id="password"
                placeholder="Current Password..."
                htmlSize={30}
              />

              <Input.Password
                {...formik.getFieldProps("newPassword")}
                status={!!formik.errors.newPassword ? "error" : undefined}
                size="large"
                id="newPassword"
                placeholder="New Password..."
              />

              <Flex gap={10} vertical>
                <Input.Password
                  {...formik.getFieldProps("confirmPassword")}
                  status={!!formik.errors.confirmPassword ? "error" : undefined}
                  size="large"
                  id="confirmPassword"
                  placeholder="Confirm Password..."
                />
                {!!formik.errors.confirmPassword && (
                  <ErrorPanel message={formik.errors.confirmPassword} />
                )}
              </Flex>
            </Flex>

            <div style={{ height: 70 }} className="mx-20">
              <Button
                type="primary"
                disabled={Object.keys(formik.errors).length > 0}
                loading={isLoading}
                onClick={() => formik.submitForm()}
              >
                Submit
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
